import dayjs from "dayjs";
import { useState } from "react";
import styled from "styled-components";

import { LinearChartSelection, PortfolioPerformanceGraphQuery, SipFrequency } from "@ctypes";
import { useGoalsContext } from "@hooks";
import { Translations, i18n } from "@i18n";
import { Card } from "@routes/DashboardRoute/Home/styles";
import { home } from "@routes/DashboardRoute/Home/tokens";
import {
  GoalStanding,
  Standing,
  buildLinearChart,
  calculateInvestedProgression,
  calculateProjectedProgression,
  formatCurrency,
} from "@utils";

const PAST_W = 392;
const PAST_H = 220;
const FUTURE_H = 220;
const FUTURE_W = 706;
const PLOT_TOP = 16;
const PLOT_BASE = 204;
const POINTS = 11;

const RANGES: Array<{ key: LinearChartSelection; label: string }> = [
  { key: "oneDay", label: "1D" },
  { key: "oneWeek", label: "1W" },
  { key: "oneMonth", label: "1M" },
  { key: "sixMonth", label: "6M" },
  { key: "oneYear", label: "1Y" },
  { key: "fiveYear", label: "All" },
];

const RANGE_DESCRIPTOR: Record<LinearChartSelection, string> = {
  oneDay: Translations.GoalsOverviewRoutePastDay,
  oneWeek: Translations.GoalsOverviewRoutePastWeek,
  oneMonth: Translations.GoalsOverviewRoutePastMonth,
  sixMonth: Translations.GoalsOverviewRoutePastSixMonths,
  oneYear: Translations.GoalsOverviewRoutePastYear,
  fiveYear: Translations.GoalsOverviewRouteAllTime,
};

const Shell = styled(Card)`
  border-radius: 22px;
  background: linear-gradient(160deg, rgba(26, 34, 52, 0.72) 0%, rgba(16, 20, 32, 0.86) 100%);
  padding: 26px 28px 24px;
  display: flex;
  align-items: stretch;
  gap: 34px;
  color: #f2efe9;

  @media (max-width: 1100px) {
    flex-direction: column;
  }
`;

const PastPanel = styled.div`
  width: ${PAST_W}px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 1100px) {
    width: 100%;
  }
`;

const FuturePanel = styled.div`
  flex-grow: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PanelHeader = styled.div`
  min-height: 96px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 10px;
`;

const PanelLabel = styled.div<{ $accent?: boolean }>`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${({ $accent }) => ($accent ? "#E09B7C" : "rgba(242,239,233,0.42)")};
`;

/* Both headline numbers share one treatment deliberately – the labels and
   the seam carry the past/future distinction, not the type. */
const Headline = styled.div`
  font-family: ${home.serif};
  font-size: 38px;
  line-height: 0.96;
  letter-spacing: -1px;
`;

const DeltaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .chip {
    display: flex;
    align-items: center;
    gap: 5px;
    height: 24px;
    padding: 0 9px;
    border-radius: 999px;
    background: rgba(242, 239, 233, 0.08);
    font-size: 12px;
    font-weight: 600;
    color: rgba(242, 239, 233, 0.72);
  }

  .descriptor {
    font-size: 12px;
    color: rgba(242, 239, 233, 0.44);
  }
`;

const Subline = styled.div`
  font-size: 13px;
  color: rgba(242, 239, 233, 0.48);
`;

const Seam = styled.div`
  width: 1px;
  flex-shrink: 0;
  position: relative;
  background: linear-gradient(
    180deg,
    rgba(242, 239, 233, 0.04) 0%,
    rgba(242, 239, 233, 0.22) 24%,
    rgba(242, 239, 233, 0.22) 82%,
    rgba(242, 239, 233, 0.04) 100%
  );

  @media (max-width: 1100px) {
    display: none;
  }
`;

const TodayPill = styled.div`
  position: absolute;
  top: 72px;
  left: 50%;
  transform: translateX(-50%);
  height: 26px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-radius: 999px;
  background: #161c2a;
  border: 1px solid rgba(242, 239, 233, 0.2);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: rgba(242, 239, 233, 0.72);
  white-space: nowrap;
`;

const DateAxis = styled.div`
  display: flex;
  align-items: flex-start;
  height: 46px;
  padding-top: 12px;
  font-size: 12px;

  .first,
  .last {
    font-weight: 500;
    color: rgba(242, 239, 233, 0.62);
  }

  .mid {
    color: rgba(242, 239, 233, 0.4);
    margin: 0 auto;
  }
`;

const RangeBar = styled.div`
  display: flex;
  gap: 3px;
  padding: 3px;
  border-radius: 999px;
  background: rgba(242, 239, 233, 0.05);
`;

const RangePill = styled.button<{ $active: boolean }>`
  flex-grow: 1;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  font-family: ${home.sans};
  font-size: 12px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "rgba(242,239,233,0.14)" : "transparent")};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  color: ${({ $active }) => ($active ? "#F2EFE9" : "rgba(242,239,233,0.50)")};
`;

const MilestoneRail = styled.div`
  position: relative;
  height: 46px;
  padding-top: 12px;
  font-size: 12px;

  .origin {
    position: absolute;
    left: 0;
    top: 12px;
    color: rgba(242, 239, 233, 0.4);
  }

  .milestone {
    position: absolute;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    gap: 3px;
    align-items: center;
    white-space: nowrap;
  }

  .milestone .name {
    font-weight: 500;
    color: rgba(242, 239, 233, 0.78);
  }

  .milestone .year {
    font-size: 11px;
    color: rgba(242, 239, 233, 0.34);
  }
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  flex-wrap: wrap;

  .item {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 12px;
  }

  .swatch {
    width: 20px;
    height: 8px;
    border-radius: 2px;
    display: block;
  }

  .ground {
    background: rgba(242, 239, 233, 0.26);
    border-top: 1.5px solid rgba(242, 239, 233, 0.5);
  }

  .sky {
    background: rgba(224, 155, 124, 0.34);
    border-top: 1.5px solid #e09b7c;
  }

  .label {
    color: rgba(242, 239, 233, 0.52);
  }

  b {
    font-weight: 600;
  }

  .added b {
    color: #e09b7c;
  }
`;

const AssumptionChips = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;

  .chip {
    display: flex;
    align-items: center;
    height: 32px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid rgba(242, 239, 233, 0.12);
    background: rgba(242, 239, 233, 0.04);
    font-size: 12px;
    font-weight: 500;
    color: rgba(242, 239, 233, 0.8);
  }
`;

type HorizonCardProps = {
  performance?: PortfolioPerformanceGraphQuery;
  standings: Array<GoalStanding>;
  annualDeposit: number;
  multiplier: number;
  contributeAfter18: boolean;
  numberOfYearsActive: number;
  maxGoalLength: number;
};

export const HorizonCard = ({
  performance,
  standings,
  annualDeposit,
  multiplier,
  contributeAfter18,
  numberOfYearsActive,
  maxGoalLength,
}: HorizonCardProps) => {
  const [range, setRange] = useState<LinearChartSelection>("oneMonth");

  const { beneficiary } = useGoalsContext();

  /* ---------- past ---------- */

  const selected = performance?.[range];
  const past = buildLinearChart({
    data: selected?.nodes,
    difference: selected?.grossReturnAbsolute,
    percentage: selected?.grossReturnPercentage,
    label: "",
    width: PAST_W,
    height: 180,
  });

  const pastArea = past.path ? `${past.path} L${PAST_W},190 L0,190 Z` : null;

  const currentValue =
    beneficiary?.goals?.reduce((sum, goal) => sum + (goal?.currentAmountInCents ?? 0), 0) ?? 0;

  const firstNode = past.nodes[0];
  const midNode = past.nodes[Math.floor(past.nodes.length / 2)];
  const lastNode = past.nodes[past.nodes.length - 1];

  const gained = past.difference >= 0;

  /* ---------- future ---------- */

  const nowYear = dayjs().year();
  const yearsRemaining = Math.max(maxGoalLength - numberOfYearsActive, 1);
  const endYear = nowYear + yearsRemaining;
  const initialBalance = currentValue / 100;

  const progressionInput = {
    amount: annualDeposit,
    recurringDepositType: SipFrequency.Annually,
    currentAge: numberOfYearsActive,
    projectedAge: numberOfYearsActive + yearsRemaining,
    contributeAfter18,
    initialBalance,
  };

  const invested = calculateInvestedProgression(progressionInput);
  const projected = calculateProjectedProgression({
    ...progressionInput,
    multiplier,
  });

  const projectedAmount = projected.at(-1)?.amount ?? 0;
  const investedAmount = invested.at(-1)?.amount ?? 0;
  const addedByTime = Math.max(projectedAmount - investedAmount, 0);

  const totalTargets = standings.reduce((sum, s) => sum + s.target, 0);
  const surplus = projectedAmount - totalTargets;

  const sample = (series: Array<{ amount: number }>, i: number) => {
    const position = (i * (series.length - 1)) / (POINTS - 1);
    const lower = Math.floor(position);
    const upper = Math.min(lower + 1, series.length - 1);
    const fraction = position - lower;
    return (series[lower]?.amount ?? 0) * (1 - fraction) + (series[upper]?.amount ?? 0) * fraction;
  };

  const max = projectedAmount || 1;
  const y = (amount: number) => PLOT_BASE - (amount / max) * (PLOT_BASE - PLOT_TOP);
  const x = (i: number) => (i / (POINTS - 1)) * FUTURE_W;

  const projectedPoints = Array.from({ length: POINTS }, (_, i) => ({
    x: x(i),
    y: y(sample(projected, i)),
  }));
  const investedPoints = Array.from({ length: POINTS }, (_, i) => ({
    x: x(i),
    y: y(sample(invested, i)),
  }));

  const toLine = (points: Array<{ x: number; y: number }>) =>
    `M${points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" L")}`;

  const projectedLine = toLine(projectedPoints);
  const investedLine = toLine(investedPoints);

  // Sky: the region between the projection and the contribution line.
  const skyArea = `${projectedLine} L${[...investedPoints]
    .reverse()
    .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(" L")} Z`;
  const groundArea = `${investedLine} L${FUTURE_W},${PLOT_BASE} L0,${PLOT_BASE} Z`;

  // Milestones: unmet goals plotted at their target year on the horizon.
  const milestones = standings
    .filter((s) => s.status !== Standing.Met && s.targetYear > nowYear && s.targetYear <= endYear)
    .sort((a, b) => a.targetYear - b.targetYear)
    .map((s) => {
      const fraction = (s.targetYear - nowYear) / (endYear - nowYear);
      const index = fraction * (POINTS - 1);
      const lower = Math.floor(index);
      const upper = Math.min(lower + 1, POINTS - 1);
      const dotY =
        projectedPoints[lower].y * (1 - (index - lower)) +
        projectedPoints[upper].y * (index - lower);

      return {
        key: s.goal.id ?? s.goal.name ?? String(s.targetYear),
        name: s.goal.name ?? "",
        year: s.targetYear,
        xPct: fraction * 100,
        xAbs: fraction * FUTURE_W,
        dotY,
        row: 0,
      };
    });

  // Labels within 18% of each other on the rail overlap – drop every
  // second colliding label onto a lower row instead.
  milestones.forEach((milestone, index) => {
    const previous = milestones[index - 1];
    if (previous && milestone.xPct - previous.xPct < 18) {
      milestone.row = previous.row === 0 ? 1 : 0;
    }
  });

  const hasStackedMilestones = milestones.some((m) => m.row > 0);

  const monthly = Math.round(annualDeposit / 12);
  const interest = Math.round((multiplier - 1) * 100);

  return (
    <Shell>
      <PastPanel>
        <PanelHeader>
          <PanelLabel>{i18n.t(Translations.GoalsOverviewRoutePastLabel)}</PanelLabel>
          <Headline>{formatCurrency(currentValue / 100, true)}</Headline>
          <DeltaRow>
            <span className="chip">
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="rgba(242,239,233,0.72)"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  transform: gained ? "rotate(180deg)" : undefined,
                }}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
              {formatCurrency(Math.abs(past.difference), true)}
            </span>
            <span className="descriptor">
              {Math.abs(past.percentage).toFixed(1)}% {i18n.t(RANGE_DESCRIPTOR[range])}
            </span>
          </DeltaRow>
        </PanelHeader>

        <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <svg
            width="100%"
            height={PAST_H}
            viewBox={`0 0 ${PAST_W} ${PAST_H}`}
            preserveAspectRatio="none"
            style={{ display: "block" }}
          >
            <defs>
              <linearGradient id="fmPastFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F2EFE9" stopOpacity="0.20" />
                <stop offset="100%" stopColor="#F2EFE9" stopOpacity="0.01" />
              </linearGradient>
            </defs>
            {pastArea && (
              <g transform="translate(0, 10)">
                <path d={pastArea} fill="url(#fmPastFill)" />
                <path
                  d={past.path ?? ""}
                  fill="none"
                  stroke="rgba(242,239,233,0.78)"
                  strokeWidth="2"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            )}
            <line
              x1="0"
              y1={PLOT_BASE}
              x2={PAST_W}
              y2={PLOT_BASE}
              stroke="rgba(242,239,233,0.16)"
              strokeWidth="1"
            />
          </svg>

          <DateAxis>
            <span className="first">
              {firstNode ? dayjs(firstNode.timestamp).format("MMM D") : ""}
            </span>
            <span className="mid">{midNode ? dayjs(midNode.timestamp).format("MMM D") : ""}</span>
            <span className="last">
              {lastNode ? dayjs(lastNode.timestamp).format("MMM D") : ""}
            </span>
          </DateAxis>
        </div>

        <RangeBar>
          {RANGES.map((item) => (
            <RangePill
              key={item.key}
              type="button"
              $active={range === item.key}
              onClick={() => setRange(item.key)}
            >
              {item.label}
            </RangePill>
          ))}
        </RangeBar>
      </PastPanel>

      <Seam>
        <TodayPill>{i18n.t(Translations.HomeToday)}</TodayPill>
      </Seam>

      <FuturePanel>
        <PanelHeader>
          <PanelLabel $accent>
            {i18n.t(Translations.GoalsOverviewRouteFutureLabel, {
              year: endYear,
            })}
          </PanelLabel>
          <Headline>{formatCurrency(projectedAmount)}</Headline>
          {totalTargets > 0 && (
            <Subline>
              {surplus >= 0
                ? i18n.t(Translations.GoalsOverviewRouteFutureBeyond, {
                    amount: formatCurrency(surplus),
                  })
                : i18n.t(Translations.GoalsOverviewRouteFutureShort, {
                    amount: formatCurrency(Math.abs(surplus)),
                  })}
            </Subline>
          )}
        </PanelHeader>

        <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <div style={{ position: "relative", height: FUTURE_H }}>
            <svg
              width="100%"
              height={FUTURE_H}
              viewBox={`0 0 ${FUTURE_W} ${FUTURE_H}`}
              preserveAspectRatio="none"
              style={{ display: "block" }}
            >
              <defs>
                <linearGradient id="fmSkyFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E09B7C" stopOpacity="0.58" />
                  <stop offset="52%" stopColor="#E09B7C" stopOpacity="0.20" />
                  <stop offset="100%" stopColor="#E09B7C" stopOpacity="0.03" />
                </linearGradient>
              </defs>

              <path d={skyArea} fill="url(#fmSkyFill)" />
              <path d={groundArea} fill="rgba(242,239,233,0.13)" />
              <path
                d={investedLine}
                fill="none"
                stroke="rgba(242,239,233,0.34)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={projectedLine}
                fill="none"
                stroke="#E09B7C"
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />

              {milestones.map((m) => (
                <line
                  key={m.key}
                  x1={m.xAbs}
                  y1={m.dotY}
                  x2={m.xAbs}
                  y2={PLOT_BASE}
                  stroke="rgba(242,239,233,0.16)"
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              <line
                x1="0"
                y1={PLOT_BASE}
                x2={FUTURE_W}
                y2={PLOT_BASE}
                stroke="rgba(242,239,233,0.16)"
                strokeWidth="1"
              />
            </svg>

            {milestones.map((m) => (
              <div
                key={m.key}
                style={{
                  position: "absolute",
                  left: `${m.xPct}%`,
                  top: (m.dotY / FUTURE_H) * 100 + "%",
                  width: 10,
                  height: 10,
                  margin: "-5px 0 0 -5px",
                  borderRadius: 999,
                  background: "#F2EFE9",
                  border: "2px solid #10141C",
                }}
              />
            ))}
          </div>

          <MilestoneRail style={hasStackedMilestones ? { height: 88 } : undefined}>
            <span className="origin">{nowYear}</span>
            {milestones.map((m) => (
              <div
                key={m.key}
                className="milestone"
                style={{
                  left: `${Math.min(Math.max(m.xPct, 6), 94)}%`,
                  top: 12 + m.row * 38,
                }}
              >
                <span className="name">{m.name}</span>
                <span className="year">{m.year}</span>
              </div>
            ))}
          </MilestoneRail>
        </div>

        <LegendRow>
          <span className="item">
            <span className="swatch ground" />
            <span className="label">{i18n.t(Translations.GoalsOverviewRouteContributed)}</span>
            <b>{formatCurrency(investedAmount)}</b>
          </span>
          <span className="item added">
            <span className="swatch sky" />
            <span className="label">{i18n.t(Translations.GoalsOverviewRouteAddedByTime)}</span>
            <b>{formatCurrency(addedByTime)}</b>
          </span>
          <AssumptionChips>
            <span className="chip">
              {i18n.t(Translations.GoalsOverviewRoutePerMonth, {
                amount: formatCurrency(monthly),
              })}
            </span>
            <span className="chip">
              {i18n.t(Translations.GoalsOverviewRouteReturnChip, {
                rate: interest,
              })}
            </span>
          </AssumptionChips>
        </LegendRow>
      </FuturePanel>
    </Shell>
  );
};
